// React and third-party libraries
import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Segment,
  Button,
  Icon,
  Input,
  Grid,
  Tab,
  Modal,
  Form,
  Checkbox,
} from "semantic-ui-react";
import Auth from "@aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";

// Relative imports - Redux actions
import { addNotification } from "../../../redux/actions/notificationActions";

// Relative imports - API
import { getColumnSetsForUserId } from "../../../apis/columnSets";

// Relative imports - Components and utilities
import { openLinks } from "./buttonHandlers/buttonHandlers";
import ColumnSetForm from "../ColumnSet/ColumnSetForm";
import PricetoolSidebar from "./PricetoolSidebar";
import PricetoolLog from "./PricetoolLog";

import FilterOptions from "./FilterOptions";
import DefaultTabSelector from "./TabSelector";
import ActiveFilters from "./ActiveFilters";
import FloatingLabelButton from "../../buttons/FloatingLabel";
import { renderDate } from "../../formatting/dateFormat";
import ListingTable from "./ListingTable";
import InventoryTable from "./InventoryTable";

// Styles
import "./Pricetool.css";

const Pricetool = (props) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const queryParams = new URLSearchParams(window.location.search);
  const allParams = Object.fromEntries(queryParams.entries());

  const inventoryTableRef = useRef(null);
  const listingTableRef = useRef(null);

  let searchDelay = useRef(null);

  const defaultTab = formatTabName(
    allParams.tab || localStorage.getItem("defaultTab") || "Listing"
  );

  function formatTabName(tabName) {
    const validTabs = ["listing", "inventory", "all"];
    const normalizedTab = (tabName || "").toLowerCase();

    if (validTabs.includes(normalizedTab)) {
      return normalizedTab.charAt(0).toUpperCase() + normalizedTab.slice(1);
    }

    return "Listing"; // Default fallback if invalid
  }
  const smallTables = defaultTab === "All" || defaultTab === undefined;
  const tableHeight = smallTables
    ? window.innerHeight / 2 - 200
    : window.innerHeight - 200;

  const [viewName, setViewName] = useState(defaultTab);
  const [listingTableHeight, setListingTableHeight] = useState(tableHeight);
  const [inventoryTableHeight, setInventoryTableHeight] = useState(tableHeight);

  const [leftSidebarVisible, setLeftSidebarVisible] = useState(false);
  const [rightSidebarVisible, setRightSidebarVisible] = useState(false);
  const [buttonShift, setButtonShift] = useState(0);

  const [searchValue, setSearchValue] = useState(
    allParams.search || props.match?.params?.search || ""
  );
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(
    allParams.search || props.match?.params?.search || ""
  );

  // Add state for the share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [displayFilterOptions, setDisplayFilterOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    retired: allParams.retired === "true" || false,
    ...(allParams.filters
      ? JSON.parse(decodeURIComponent(allParams.filters))
      : {}),
  });

  // Url share state
  const [includeFamilyUrl, setIncludeFamilyUrl] = useState(true);

  // Combined state for column set
  const [currentColumnSet, setCurrentColumnSet] = useState({
    name: null,
    columns: [],
  });

  const [columnSets, setColumnSets] = useState([]);

  const [outputLog, setOutputLog] = useState([]);
  const [outputLogClearedTimestamp, setOutputLogClearedTimestamp] =
    useState(null);

  const setDefaultTab = (tabName) => {
    localStorage.setItem("defaultTab", tabName);
    setViewName(tabName);

    dispatch(
      addNotification({
        icon: <Icon color="green" name="checkmark box" />,
        text: `${tabName} tab set as default`,
      })
    );
  };

  const toggleTableView = (viewName) => {
    switch (viewName) {
      case "Listing":
        setViewName("Listing");
        setListingTableHeight(window.innerHeight);
        break;

      case "Inventory":
        setViewName("Inventory");
        setInventoryTableHeight(window.innerHeight);
        break;

      case "All":
        setViewName("All");
        setListingTableHeight(window.innerHeight / 2 - 200);
        setInventoryTableHeight(window.innerHeight / 2 - 200);
        break;

      default:
        console.warn(`Unexpected view name: ${viewName}`);
    }
  };

  const clearOutputLog = (fullClear = false) => {
    setOutputLogClearedTimestamp(renderDate(Math.floor(Date.now() / 1000)));

    if (fullClear) {
      setOutputLog([]);
    } else {
      setOutputLog((prev) => prev.filter((_, i) => i === prev.length - 1));
    }
  };

  const handleSetColumnSet = useCallback(
    (columnSetName) => {
      const columnSet = columnSets.find(
        (set) => set.columnSetName === columnSetName
      );

      if (columnSet) {
        setCurrentColumnSet((_) => ({
          name: columnSetName,
          columns: columnSet.columns,
        }));
      } else {
        dispatch(
          addNotification({
            icon: <Icon color="yellow" name="warning sign" />,
            text: `Column set "${columnSetName}" not found. Using default columns.`,
          })
        );

        setCurrentColumnSet((_) => ({
          name: null,
          columns: [],
        }));
      }
    },
    [columnSets, dispatch]
  );

  const handleColumnSetReset = () => {
    setCurrentColumnSet((_) => ({
      name: null,
      columns: [],
    }));
  };

  useEffect(() => {
    const initializeComponent = async () => {
      await Auth.currentAuthenticatedUser();

      if (auth.userId) {
        const fetchedColumnSets = await getColumnSetsForUserId(auth.userId);
        setColumnSets(fetchedColumnSets);
      }
    };

    initializeComponent();
  }, [auth.userId]);

  useEffect(() => {
    if (columnSets.length > 0 && allParams.colset) {
      handleSetColumnSet(allParams.colset);
    }
  }, [columnSets, allParams.colset, handleSetColumnSet]);

  const getShareUrl = () => {
    const params = new URLSearchParams();

    // Get all current parameters first
    const currentParams = new URLSearchParams(window.location.search);

    // Keep the colset parameter if it exists in the URL but isn't yet in state
    const colsetParam = currentParams.get("colset");

    if (searchValue) params.set("search", searchValue);
    params.set("tab", viewName);

    if (
      includeFamilyUrl &&
      (inventoryTableRef?.current || listingTableRef?.current)
    ) {
      if (viewName !== "Listing" && inventoryTableRef?.current) {
        const inventoryTable = inventoryTableRef.current;
        const inventoryFamilyValue = inventoryTable
          .column("supply_families_view.families:name")
          .search();
        if (inventoryFamilyValue)
          params.set(
            `inventoryColumnSearches[supply_families_view.families]`,
            inventoryFamilyValue
          );
      }

      if (viewName !== "Inventory" && listingTableRef?.current) {
        const listingTable = listingTableRef.current;

        const listingFamilyValue = listingTable
          .column("product_families_view.families:name")
          .search();
        if (listingFamilyValue)
          params.set(
            `listingColumnSearches[product_families_view.families]`,
            listingFamilyValue
          );
      }
    }

    if (Object.values(filterOptions).some((val) => val)) {
      Object.entries(filterOptions).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });
    }

    // If we have a column set name in state, use that
    if (currentColumnSet.name) {
      params.set("colset", currentColumnSet.name);
    }
    // Otherwise, if there's a colset in the URL and we haven't processed it yet, keep it
    else if (
      colsetParam &&
      !columnSets.some((set) => set.columnSetName === colsetParam)
    ) {
      params.set("colset", colsetParam);
    }

    const newUrl = `${window.location.origin}${
      window.location.pathname
    }?${params.toString()}`;

    return newUrl;
  };

  const onChangeSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);

    clearTimeout(searchDelay.current);

    searchDelay.current = setTimeout(() => {
      setDebouncedSearchValue(value.trim());
      setSearchValue(value.trim());
    }, 2000);
  };

  const handleSubmit = (e) => {
    if (e.key === "Enter") {
      clearTimeout(searchDelay.current);
      setDebouncedSearchValue(searchValue);
    }
  };

  const openPricetoolLink = (store) => {
    if (inventoryTableRef.current) {
      openLinks(inventoryTableRef.current, store);
    }
  };

  const filterCount = () => {
    let count = 0;
    Object.values(filterOptions).forEach((val) => (val ? (count += 1) : null));
    return count;
  };

  const filtersAreActive = () => {
    return Object.values(filterOptions).some((value) => value);
  };

  const appendOutputLog = (newLog) => {
    setOutputLog((prev) => [...prev, newLog]);
  };

  // Function to copy URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(getShareUrl())
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);

        dispatch(
          addNotification({
            icon: <Icon color="green" name="checkmark box" />,
            text: "Share link copied to clipboard",
          })
        );
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);

        dispatch(
          addNotification({
            icon: <Icon color="red" name="warning circle" />,
            text: "Failed to copy link",
          })
        );
      });
  };

  const setListingTableRef = (datatable) => {
    listingTableRef.current = datatable;
  };

  const setInventoryTableRef = (datatable) => {
    inventoryTableRef.current = datatable;
  };

  const panes = [
    {
      menuItem: "Listing",
      render: () => (
        <Tab.Pane>
          <ListingTable
            setParentComponentTableRef={setListingTableRef}
            filterOptions={filterOptions}
            match={props.match}
            tableHeight={listingTableHeight}
            currentColumnSet={currentColumnSet}
            searchValue={debouncedSearchValue}
            appendOutputLog={appendOutputLog}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Inventory",
      render: () => (
        <Tab.Pane>
          <InventoryTable
            setParentComponentTableRef={setInventoryTableRef}
            match={props.match}
            tableHeight={inventoryTableHeight}
            currentColumnSet={currentColumnSet}
            searchValue={debouncedSearchValue}
            appendOutputLog={appendOutputLog}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "All",
      render: () => (
        <Tab.Pane>
          <div>
            <ListingTable
              setParentComponentTableRef={setListingTableRef}
              inventoryTableRef={inventoryTableRef}
              filterOptions={filterOptions}
              match={props.match}
              tableHeight={listingTableHeight}
              currentColumnSet={currentColumnSet}
              searchValue={debouncedSearchValue}
              appendOutputLog={appendOutputLog}
            />
            <InventoryTable
              setParentComponentTableRef={setInventoryTableRef}
              match={props.match}
              tableHeight={inventoryTableHeight}
              currentColumnSet={currentColumnSet}
              searchValue={debouncedSearchValue}
              appendOutputLog={appendOutputLog}
            />
          </div>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Fragment>
      {/* Share Link Modal */}
      <Modal
        size="small"
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
      >
        <Modal.Header>Share View Link</Modal.Header>
        <Modal.Content>
          <p>Copy this link to share your current view with others:</p>
          <Form>
            <Form.Field>
              <Input
                fluid
                value={getShareUrl()}
                readOnly
                action={{
                  color: copySuccess ? "green" : "blue",
                  labelPosition: "right",
                  icon: copySuccess ? "check" : "copy",
                  content: copySuccess ? "Copied!" : "Copy",
                  onClick: copyToClipboard,
                }}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                checked={includeFamilyUrl}
                label="Include family searches"
                onChange={() => setIncludeFamilyUrl((prev) => !prev)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="grey" onClick={() => setShareModalOpen(false)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>

      <FilterOptions
        filterOptions={filterOptions}
        updateFilters={(filters) => {
          setFilterOptions(filters);
        }}
        isOpen={displayFilterOptions}
        onClose={() => setDisplayFilterOptions(false)}
      />

      <PricetoolSidebar
        leftSidebarVisible={leftSidebarVisible}
        rightSidebarVisible={rightSidebarVisible}
        closeLeftSidebar={() => setLeftSidebarVisible(false)}
        closeRightSidebarToggle={() => setRightSidebarVisible(false)}
        clearSidebar={clearOutputLog}
        openPricetoolLink={openPricetoolLink}
        inventoryTableRef={inventoryTableRef}
        content={
          <PricetoolLog
            content={outputLog}
            clearedTimestamp={outputLogClearedTimestamp}
          />
        }
      >
        <Segment>
          <Grid>
            <Grid.Row columns={3}>
              <Grid.Column width={6}>
                <Button
                  icon
                  onMouseEnter={() => {
                    setLeftSidebarVisible(true);
                  }}
                >
                  <Icon name="list" />
                </Button>
                <Input
                  placeholder="Search ..."
                  value={searchValue}
                  onChange={onChangeSearch}
                  onKeyDown={handleSubmit}
                  autoComplete="off"
                  style={{ margin: "0 12px" }}
                />
                <FloatingLabelButton
                  label="More Filters"
                  labelValue={filterCount()}
                  color={filtersAreActive() ? "blue" : null}
                  onClick={() => setDisplayFilterOptions(true)}
                  style={{ marginRight: "10px" }}
                />
                {filtersAreActive() && (
                  <ActiveFilters filters={filterOptions} />
                )}
                <div style={{ marginLeft: "12px", display: "inline-block" }}>
                  <Button
                    icon="external share"
                    title="Create share link for view"
                    onClick={() => setShareModalOpen(true)}
                  />
                </div>
              </Grid.Column>

              <Grid.Column width={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "52px",
                  }}
                >
                  <DefaultTabSelector
                    currentTab={viewName}
                    onSetDefault={setDefaultTab}
                  />
                </div>
              </Grid.Column>

              <Grid.Column width={6}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <ColumnSetForm
                    columnSets={columnSets}
                    handleReorder={handleSetColumnSet}
                    handleReset={handleColumnSetReset}
                  />

                  <div style={{ marginLeft: "12px" }}>
                    <Button
                      icon
                      onClick={() => {
                        if (rightSidebarVisible) {
                          setButtonShift(0);
                        } else {
                          setButtonShift(350);
                        }
                        setRightSidebarVisible(!rightSidebarVisible);
                      }}
                      style={{
                        transform: `translate(${-buttonShift}px)`,
                        transition: "0.5s",
                      }}
                    >
                      <Icon name="list" />
                    </Button>
                  </div>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        <Tab
          panes={panes}
          activeIndex={
            viewName === "Listing"
              ? 0
              : viewName === "Inventory"
              ? 1
              : viewName === "All"
              ? 2
              : 0
          }
          onTabChange={(e, data) => {
            const viewNames = ["Listing", "Inventory", "All"];
            toggleTableView(viewNames[data.activeIndex]);
          }}
        />
      </PricetoolSidebar>
    </Fragment>
  );
};

export default Pricetool;
